import classNames from 'classnames'
import { FC, FormEvent, ReactNode, useState } from 'react'
import {
  AiModelMultiSelect,
  AiModelOption,
} from 'src/components/AiModelMultiSelect'
import { AiTool } from 'src/services/aiListeningApi/brandProfile.types'
import styles from './profileForm.module.scss'
import { ChipsInput } from '../ChipsInput'
import { BuildingIcon } from '../ProfileHeader'

export interface ProfileFormValues {
  aiTools: AiTool[]
  aliases: string[]
  brandName: string
  competitors: string[]
  name: string
  website: string
}

type Field = keyof ProfileFormValues
type FormErrors = Partial<Record<Field, string>>

// brandNames holds at most 20 spellings, and brandName is one of them.
const MAX_ALIASES = 19
const MAX_NAME_LENGTH = 200

const EMPTY_VALUES: ProfileFormValues = {
  aiTools: [],
  aliases: [],
  brandName: '',
  competitors: [],
  name: '',
  website: '',
}

// "silknet.com" → "https://silknet.com"; an empty field stays empty.
export const normalizeWebsite = (website: string): string => {
  const trimmed = website.trim()
  if (!trimmed) return ''
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

const isValidWebsite = (website: string): boolean => {
  try {
    const { hostname } = new URL(normalizeWebsite(website))
    return hostname.includes('.') && !hostname.startsWith('.')
  } catch {
    return false
  }
}

const validate = (
  values: ProfileFormValues,
  variant: ProfileFormProps['variant']
): FormErrors => {
  const errors: FormErrors = {}
  const isBrand = variant === 'brand'

  if (!values.name.trim()) {
    errors.name = 'Enter a profile name'
  } else if (values.name.trim().length > MAX_NAME_LENGTH) {
    errors.name = `Keep the profile name under ${MAX_NAME_LENGTH} characters`
  }
  if (!values.brandName.trim()) {
    errors.brandName = 'Enter the brand name'
  } else if (values.brandName.trim().length > MAX_NAME_LENGTH) {
    errors.brandName = `Keep the brand name under ${MAX_NAME_LENGTH} characters`
  }
  if (isBrand && values.aiTools.length === 0) {
    errors.aiTools = 'Choose at least one AI model'
  }
  if (isBrand && !values.website.trim()) {
    errors.website = 'Enter the brand’s website'
  } else if (values.website.trim() && !isValidWebsite(values.website)) {
    errors.website = 'Enter a valid website address, e.g. https://brand.com'
  }
  if (values.aliases.length > MAX_ALIASES) {
    errors.aliases = `Add up to ${MAX_ALIASES} alternative names`
  }
  return errors
}

interface ProfileFormProps {
  isSubmitting: boolean
  onCancel: () => void
  onSubmit: (values: ProfileFormValues) => void
  // Competitors are measured on their brand's models: shown, not chosen.
  readOnlyAiTools?: AiTool[]
  // A message from the server worth showing beside the fields (a duplicate
  // name, a rejected website).
  submitError?: string
  subtitle: string
  title: string
  variant: 'brand' | 'competitor'
}

interface FieldProps {
  children: ReactNode
  error?: string
  hint?: string
  htmlFor: string
  label: ReactNode
}

const FormField: FC<FieldProps> = ({
  children,
  error,
  hint,
  htmlFor,
  label,
}) => (
  <div className={styles.field}>
    <label className={styles.label} htmlFor={htmlFor}>
      {label}
    </label>
    {children}
    {error ? (
      <span className={styles.error} role="alert">
        {error}
      </span>
    ) : (
      hint && <span className={styles.hint}>{hint}</span>
    )}
  </div>
)

const SparkleIcon: FC = () => (
  <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
    <path
      d="M7 2.5l1.1 3.4 3.4 1.1-3.4 1.1L7 11.5 5.9 8.1 2.5 7l3.4-1.1L7 2.5zM12.5 10l.5 1.5 1.5.5-1.5.5-.5 1.5-.5-1.5-1.5-.5 1.5-.5.5-1.5z"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="1"
    />
  </svg>
)

const SwordsIcon: FC = () => (
  <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
    <path
      d="M2.5 2.5l7 7M2.5 2.5H5l6 6M13.5 2.5l-7 7M13.5 2.5H11L9.5 4M4 10l2 2M3 13l2-2M12 10l-2 2M13 13l-2-2"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.1"
    />
  </svg>
)

// The Create Brand Profile / Create Competitor's Profile form. Errors show
// under their field once the user has left it or tried to submit.
export const ProfileForm: FC<ProfileFormProps> = ({
  isSubmitting,
  onCancel,
  onSubmit,
  readOnlyAiTools,
  submitError,
  subtitle,
  title,
  variant,
}) => {
  const isBrand = variant === 'brand'
  const [values, setValues] = useState<ProfileFormValues>(EMPTY_VALUES)
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({})
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const errors = validate(values, variant)
  const visibleError = (field: Field) =>
    hasSubmitted || touched[field] ? errors[field] : undefined

  const set = <K extends Field>(field: K, value: ProfileFormValues[K]) =>
    setValues((previous) => ({ ...previous, [field]: value }))
  const touch = (field: Field) =>
    setTouched((previous) => ({ ...previous, [field]: true }))

  const isComplete =
    !!values.name.trim() &&
    !!values.brandName.trim() &&
    (!isBrand || (values.aiTools.length > 0 && !!values.website.trim()))

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setHasSubmitted(true)
    if (Object.keys(errors).length > 0 || isSubmitting) return
    onSubmit({
      ...values,
      brandName: values.brandName.trim(),
      name: values.name.trim(),
      website: normalizeWebsite(values.website),
    })
  }

  return (
    <form className={styles.card} noValidate onSubmit={handleSubmit}>
      <div className={styles.heading}>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>

      <div className={styles.panel}>
        <div className={styles.fields}>
          <FormField
            error={visibleError('name')}
            htmlFor="ai-profile-name"
            label={isBrand ? 'Profile Name' : 'Competitor’s Profile Name'}
          >
            <input
              className={classNames(styles.input, {
                [styles.inputError]: !!visibleError('name'),
              })}
              id="ai-profile-name"
              onBlur={() => touch('name')}
              onChange={(event) => set('name', event.target.value)}
              placeholder={
                isBrand
                  ? 'Enter a profile name'
                  : 'Enter competitor’s profile name'
              }
              type="text"
              value={values.name}
            />
          </FormField>

          <FormField
            error={visibleError('brandName')}
            hint="You can enter the brand name in multiple languages"
            htmlFor="ai-profile-brand"
            label="Brand Name"
          >
            <input
              className={classNames(styles.input, {
                [styles.inputError]: !!visibleError('brandName'),
              })}
              id="ai-profile-brand"
              onBlur={() => touch('brandName')}
              onChange={(event) => set('brandName', event.target.value)}
              placeholder="Enter a brand name"
              type="text"
              value={values.brandName}
            />
          </FormField>

          <FormField
            error={visibleError('aiTools')}
            hint={
              readOnlyAiTools
                ? 'Competitors are measured on your brand’s prompts and models'
                : undefined
            }
            htmlFor="ai-profile-models"
            label="AI Model"
          >
            <AiModelMultiSelect
              disabled={!!readOnlyAiTools}
              hasError={!!visibleError('aiTools')}
              id="ai-profile-models"
              onChange={(next: AiModelOption[]) => {
                set('aiTools', next)
                touch('aiTools')
              }}
              value={readOnlyAiTools ?? values.aiTools}
            />
          </FormField>

          <FormField
            error={visibleError('website')}
            htmlFor="ai-profile-website"
            label="Website"
          >
            <input
              className={classNames(styles.input, {
                [styles.inputError]: !!visibleError('website'),
              })}
              id="ai-profile-website"
              inputMode="url"
              onBlur={() => touch('website')}
              onChange={(event) => set('website', event.target.value)}
              placeholder="Enter a website address"
              type="text"
              value={values.website}
            />
          </FormField>

          <span className={styles.optional}>Optional</span>

          <FormField
            error={visibleError('aliases')}
            htmlFor="ai-profile-aliases"
            label={
              <span className={styles.labelWithIcon}>
                <BuildingIcon />
                Brand Aliases / Previous Names
              </span>
            }
          >
            <span className={styles.fieldNote}>
              Alternative or former names AI models might use for this brand.
              We&apos;ll also recognize additional variations automatically
            </span>
            <ChipsInput
              emptyPlaceholder="Enter a previous brand name"
              id="ai-profile-aliases"
              onChange={(next) => {
                set('aliases', next)
                touch('aliases')
              }}
              value={values.aliases}
            />
          </FormField>

          {isBrand && (
            <FormField
              htmlFor="ai-profile-competitors"
              label={
                <span className={styles.labelWithIcon}>
                  <SwordsIcon />
                  Tracked Competitors
                </span>
              }
            >
              <span className={styles.fieldNote}>
                Competitors you want to prioritize tracking. Hapttic will also
                surface others it finds in AI responses
              </span>
              <ChipsInput
                emptyPlaceholder="Enter a competitor"
                id="ai-profile-competitors"
                onChange={(next) => set('competitors', next)}
                value={values.competitors}
              />
              <span className={styles.discoveredNote}>
                <SparkleIcon />
                AI-discovered competitors will appear separately in reporting,
                labeled by source
              </span>
            </FormField>
          )}
        </div>
      </div>

      {submitError && (
        <p className={styles.submitError} role="alert">
          {submitError}
        </p>
      )}

      <div className={styles.actions}>
        <button className={styles.cancel} onClick={onCancel} type="button">
          Cancel
        </button>
        <button
          className={styles.submit}
          disabled={!isComplete || isSubmitting}
          type="submit"
        >
          {isSubmitting && 'Creating…'}
          {!isSubmitting &&
            (isBrand ? 'Create Brand Profile' : 'Add Competitor')}
        </button>
      </div>
    </form>
  )
}
